<script setup lang="ts">
import { PostState } from "@/composition/posts";
import { LiveState, LiveStreamType } from "@/helper/go.live.helper";
import { GoLiveStatus, IPosts, PostType } from "@/models/Posts";
import { MediaService } from "@/service/MediaService";
import { getNumberAbbreviation } from "@/util/NumberUtils";
import { EyeIcon, VideoRecorderIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UITextLgMedium, UITextSmMedium } from "@gohighlevel/ghl-ui";
import { PropType, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["recording-available"]);
const _props = defineProps({
  post: {
    type: Object as PropType<IPosts>,
    required: true,
  },
});

const { t } = useI18n();
const membersCount = ref<number>(0);
const isLoading = ref<boolean>(true);
const isStreaming = ref<boolean>(false);
const recordingState = ref<string>("");

const handleRoomStatus = async () => {
  if (
    _props.post?.metadata?.type == LiveStreamType.ROOM &&
    _props.post.postType === PostType.GO_LIVE &&
    _props.post.metadata?.status !== GoLiveStatus.RECORDING_AVAILABLE
  ) {
    isLoading.value = true;
    try {
      const response = await MediaService.getStreamLiveState(
        _props.post?.metadata?.licenseId as string
      );
      membersCount.value = response.peakAttendance || 0;
      isStreaming.value = response.state === LiveState.LIVE;
      recordingState.value = response.recordingState || "";
      const goLiveStatus =
        response.recordingState === "completed"
          ? GoLiveStatus.RECORDING_AVAILABLE
          : response.state === LiveState.COMPLETED
          ? GoLiveStatus.STREAM_COMPLETED
          : GoLiveStatus.STREAM_STARTED;
      // If live status is completed and recording is available, update the post status
      if ((_props.post.metadata?.status as GoLiveStatus) !== goLiveStatus) {
        try {
          await PostState.updatePostStatus(_props.post, goLiveStatus);
        } catch (error) {
          console.error("Unable to update post status", error);
        }
      }

      if (response.recordingState === "completed") {
        emit("recording-available");
      }
    } catch (error) {
      console.error("Unable to get room status", error);
    } finally {
      isLoading.value = false;
    }
  }
};

onMounted(() => {
  handleRoomStatus();
});
</script>
<template>
  <div class="live-stream-card">
    <!-- Top Section -->
    <div v-if="!isLoading && isStreaming" class="card-header">
      <div class="live-badge">
        <div class="live-dot" />
        <span class="live-text uppercase">{{ t("golive.live") }}</span>
      </div>
      <div class="watching-badge">
        <EyeIcon class="h-5 w-5" />
        <span
          >{{ getNumberAbbreviation(membersCount) }}
          {{ t("golive.watching") }}</span
        >
      </div>
    </div>
    <div
      v-if="!isLoading && !isStreaming && recordingState"
      class="card-header flex flex-col"
    >
      <UITextLgMedium class="text-[20px] text-xl font-[900] text-[#fcfcfd]">
        {{
          recordingState === "failed"
            ? t("golive.recordingFailed")
            : t("golive.processingRecording")
        }}
      </UITextLgMedium>
      <UITextSmMedium class="py-6 text-[#ffffff]">
        {{
          recordingState === "failed"
            ? t("golive.recordingFailedDescription")
            : t("golive.streamEndedRecordingWillBeAvailableSoon")
        }}
      </UITextSmMedium>
    </div>
    <div
      v-if="!isLoading && !isStreaming && !recordingState"
      class="card-header flex flex-col"
    >
      <UITextSmMedium class="py-6 text-[#ffffff]">
        {{ t("golive.noParticipantOpenCamOrMic") }}
      </UITextSmMedium>
    </div>

    <!-- Bottom Section - Join Button -->
    <div v-if="!isLoading" class="card-actions">
      <button class="join-stream-btn" :disabled="!isStreaming">
        <VideoRecorderIcon
          class="h-5 w-5"
          :class="{
            'text-[#242526]': !isStreaming,
            'text-[#344054]': isStreaming,
          }"
        />
        <UITextSmMedium
          :class="{
            'text-[#242526]': !isStreaming,
            'text-[#344054]': isStreaming,
          }"
          >{{
            isStreaming
              ? t("golive.joinStream")
              : recordingState === "failed"
              ? t("golive.recordingNotAvailable")
              : t("golive.recordingInProgress")
          }}</UITextSmMedium
        >
      </button>
    </div>
  </div>
</template>

<style scoped>
.live-stream-card {
  display: flex;
  width: 100%;
  height: 100%;
  padding: 12px 16px 16px 16px;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  border-radius: 16px;
  background: linear-gradient(
    90deg,
    #0f0c29 0.03%,
    #302b63 50%,
    #24243e 99.97%
  );
  background-clip: padding-box;
  position: relative;
  box-sizing: border-box;
  min-height: 134px;
}

.card-header {
  display: flex;
  gap: 8px;
  width: 100%;
}

.live-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background-color: #ffffff;
  border: 2px solid #ef4444;
  border-radius: 100px;
}

.live-dot {
  width: 8px;
  height: 8px;
  background-color: #ef4444;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.live-text {
  color: #ef4444;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
}

.watching-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #ffffff;
  padding: 4px 12px;
  border-radius: 100px;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  border: 2px solid #d0d5dd;
}

.watching-badge svg {
  width: 20px;
  height: 20px;
  color: #374151;
}

.card-description {
  width: 100%;
  position: relative;
  z-index: 1;
}

.card-description p {
  color: #374151;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  margin: 0;
  text-align: left;
}

.card-actions {
  position: relative;
  z-index: 1;
  max-width: 450px;
}

.join-stream-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  color: #111827;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.join-stream-btn svg {
  width: 20px;
  height: 20px;
  color: #111827;
}

.join-stream-btn:disabled {
  background-color: #716f87;
  color: #242526;
  cursor: not-allowed;
}

.join-stream-btn:disabled svg {
  color: #111827;
}

.join-stream-btn:hover:not(:disabled) {
  background-color: #f9fafb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.join-stream-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .live-stream-card {
    width: 100%;
    max-width: 450px;
    margin: 0 auto;
  }
}
</style>
